import { curvePoint, curveTangent } from './path-curves.js';
import { pathColor } from './shortest-paths.js';

// Conservative rotated-text AABBs share the same collision grid as person
// labels. This sacrifices density rather than allowing diagonal overlaps.
export function rotatedBox(x, y, width, height, angle) {
  const dx = Math.abs(Math.cos(angle)) * width / 2 + Math.abs(Math.sin(angle)) * height / 2;
  const dy = Math.abs(Math.sin(angle)) * width / 2 + Math.abs(Math.cos(angle)) * height / 2;
  return { left: x - dx, right: x + dx, top: y - dy, bottom: y + dy };
}
export function relationCandidates(a, b, textWidth, fontSize, previous, curve = null) {
  const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy);
  if (length < textWidth + 24) return [];
  const candidates = [];
  for (const offset of [-10, 10, -23, 23, -36, 36]) for (const fraction of [.5, .35, .65, .2, .8, .1, .9]) {
    if (length * Math.min(fraction, 1 - fraction) < textWidth / 2 + 8) continue;
    const point = curve ? curvePoint(curve, fraction) : { x: a.x + dx * fraction, y: a.y + dy * fraction };
    const tangent = curve ? curveTangent(curve, fraction) : { x: dx, y: dy };
    const norm = Math.hypot(tangent.x, tangent.y) || 1;
    const raw = Math.atan2(tangent.y, tangent.x), reversed = raw > Math.PI / 2 || raw < -Math.PI / 2;
    const angle = reversed ? raw + (raw > 0 ? -Math.PI : Math.PI) : raw;
    const x = point.x - tangent.y / norm * offset, y = point.y + tangent.x / norm * offset;
    candidates.push({ x, y, angle, reversed, fraction, offset, ...rotatedBox(x, y, textWidth + 8, fontSize + 8, angle) });
  }
  if (previous) candidates.sort((a, b) => Number(b.fraction === previous.fraction && b.offset === previous.offset) - Number(a.fraction === previous.fraction && a.offset === previous.offset));
  return candidates;
}
export function drawPathEdgeLabels({ context, graph, renderer, paths, mobile, width, height, collides, reserve, obstacles, intersects, previous, curves = new Map() }) {
  if (!paths) return [];
  const groups = new Map(), visible = [], fontSize = mobile ? 10.5 : 11;
  const font = `500 ${fontSize}px ${renderer.getSetting('labelFont')}`;
  context.font = font;
  // Parallel factual relations share one annotation instead of overprinting.
  for (const [edge, mask] of paths.edges) {
    if (!graph.hasEdge(edge)) continue;
    const [source, target] = graph.extremities(edge), key = [source, target].sort().join(':');
    if (!groups.has(key)) groups.set(key, { id: edge, ids: [], source, target, mask: 0, labels: [] });
    const group = groups.get(key), data = graph.getEdgeAttributes(edge);
    group.labels.push({ label: data.label, past: data.temporalStatus === 'PAST', directed: graph.isDirected(edge), source });
    group.ids.push(edge); group.mask |= mask;
  }
  for (const group of [...groups.values()].sort((a, b) => a.id.localeCompare(b.id))) {
    const curve = curves.get(group.id);
    const a = curve?.a || renderer.graphToViewport(graph.getNodeAttributes(group.source));
    const b = curve?.b || renderer.graphToViewport(graph.getNodeAttributes(group.target));
    const textFor = reversed => [...new Set(group.labels.map(item => {
      const forward = item.source === (curve?.source || group.source);
      const arrow = item.directed ? (forward !== reversed ? ' →' : ' ←') : '';
      return item.label + arrow + (item.past ? ' (passé)' : '');
    }))].sort().join(' · ');
    const textWidth = context.measureText(textFor(false)).width;
    const options = relationCandidates(a, b, textWidth, fontSize, previous.get(group.id), curve);
    const box = options.find(box => box.left >= 0 && box.right <= width && box.top >= 0 && box.bottom <= height
      && !obstacles.some(other => intersects(box, other, 3)) && !collides(box, 3));
    if (!box) continue;
    const text = textFor(box.reversed);
    reserve(box);
    context.save(); context.translate(box.x, box.y); context.rotate(box.angle);
    context.font = font; context.textAlign = 'center'; context.textBaseline = 'middle';
    context.fillStyle = '#0b0f10'; context.fillRect(-textWidth / 2 - 3, -fontSize / 2 - 3, textWidth + 6, fontSize + 6);
    context.fillStyle = pathColor(group.mask); context.fillText(text, 0, 0); context.restore();
    visible.push({ ...box, id: group.id, ids: group.ids, text, fontSize });
  }
  return visible;
}
